import { auth } from '../firebase';
import { createAuthHeaders } from '../api-client';

const API_URL = import.meta.env.VITE_API_URL;

export interface CustomDomain {
  id: string;
  domain: string;
  isPrimary: boolean;
  status: 'pending' | 'verifying' | 'active' | 'failed' | 'disabled';
  sslStatus: 'pending_validation' | 'active' | 'pending_deployment' | 'deleted';
  dnsRecords: Array<{
    type: string;
    name: string;
    value: string;
    ttl: number;
    purpose?: string;
  }>;
  verificationErrors?: string[];
  sslExpiresAt?: string;
  createdAt: string;
}

export const customDomainsApi = {
  async create(projectId: string, domain: string, isPrimary = false): Promise<CustomDomain> {
    const token = await auth.getIdToken();
    if (!token) throw new Error('Not authenticated');
    const response = await fetch(`${API_URL}/api/projects/${projectId}/custom-domains`, {
      method: 'POST',
      headers: {
        ...createAuthHeaders(token),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ domain, isPrimary }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to add custom domain');
    }

    return response.json();
  },

  async list(projectId: string): Promise<CustomDomain[]> {
    const token = await auth.getIdToken();
    if (!token) throw new Error('Not authenticated');
    const response = await fetch(`${API_URL}/api/projects/${projectId}/custom-domains`, {
      headers: createAuthHeaders(token),
    });

    if (!response.ok) {
      throw new Error('Failed to load custom domains');
    }

    return response.json();
  },

  async verify(projectId: string, domainId: string): Promise<CustomDomain> {
    const token = await auth.getIdToken();
    if (!token) throw new Error('Not authenticated');
    const response = await fetch(
      `${API_URL}/api/projects/${projectId}/custom-domains/${domainId}/verify`,
      {
        method: 'POST',
        headers: createAuthHeaders(token),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to verify domain');
    }

    return response.json();
  },

  async setPrimary(projectId: string, domainId: string): Promise<CustomDomain> {
    const token = await auth.getIdToken();
    if (!token) throw new Error('Not authenticated');
    const response = await fetch(
      `${API_URL}/api/projects/${projectId}/custom-domains/${domainId}/set-primary`,
      {
        method: 'POST',
        headers: createAuthHeaders(token),
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to set primary domain' }));
      throw new Error(error.message || 'Failed to set primary domain');
    }

    return response.json();
  },

  async unsetPrimary(projectId: string): Promise<void> {
    const token = await auth.getIdToken();
    if (!token) throw new Error('Not authenticated');
    const response = await fetch(
      `${API_URL}/api/projects/${projectId}/custom-domains/unset-primary`,
      {
        method: 'POST',
        headers: createAuthHeaders(token),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to unset primary domain');
    }
  },

  async delete(projectId: string, domainId: string): Promise<void> {
    const token = await auth.getIdToken();
    if (!token) throw new Error('Not authenticated');
    const response = await fetch(
      `${API_URL}/api/projects/${projectId}/custom-domains/${domainId}`,
      {
        method: 'DELETE',
        headers: createAuthHeaders(token),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to delete custom domain');
    }
  },
};
